import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Plus, Search, Workflow, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { formatAutomationModeLabel } from '@apps/web/components/automations/admin_automation_helpers';
import { AutomationGroupActions } from '@apps/web/components/automations/automation_actions';
import {
    formatErrorMessage,
    useAdminAgentAutomations,
} from '@apps/web/components/automations/automation_api';
import { AutomationBuilderDialog } from '@apps/web/components/automations/automation_builder_dialog';
import {
    AUTOMATION_PAGE_SIZE,
    formatOptionalDate,
    triggerSummary,
} from '@apps/web/components/automations/automation_form';
import { StatusBadge } from '@apps/web/components/automations/badges';
import {
    type AdminAgentAutomationGroupRead,
    type AgentAutomationStatus,
    type AutomationTargetPair,
} from '@apps/web/components/automations/types';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { useAllAdminOrganizations } from '@apps/web/swr_data_fetching/admin_organization_queries';
import { type Mode } from '@libraries/typescript/api_client';

const PER_PAGE = AUTOMATION_PAGE_SIZE;
const STATUS_OPTIONS = ['active', 'paused'] as const;
const MODE_OPTIONS = ['live', 'sandbox'] as const;
const TRIGGER_TYPE_OPTIONS = ['schedule', 'phoebe_event'] as const;
type AgentAutomationTriggerType = (typeof TRIGGER_TYPE_OPTIONS)[number];

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

const searchSchema = z.object({
    page: z.coerce.number().optional().default(1),
    organization_id: arrayParam(z.string()),
    status: arrayParam(z.enum(STATUS_OPTIONS)),
    mode: arrayParam(z.enum(MODE_OPTIONS)),
    trigger_type: arrayParam(z.enum(TRIGGER_TYPE_OPTIONS)),
    search: z.string().optional(),
});

type AdminAutomationsSearch = z.infer<typeof searchSchema>;
type BuilderIntent = 'create' | 'clone' | 'add_targets';

export const Route = createFileRoute('/_app/admin/agent/automations/list')({
    head: () => ({
        meta: [
            {
                title: 'Agent Automations | Phoebe Admin',
            },
        ],
    }),
    validateSearch: searchSchema,
    component: AdminAutomationsPage,
});

function getSingleValue<T>(
    values: ReadonlyArray<T> | undefined,
): T | undefined {
    return values?.length === 1 ? values[0] : undefined;
}

function AdminAutomationsPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const [builderOpen, setBuilderOpen] = useState(false);
    const [createTargetPairs, setCreateTargetPairs] = useState<
        Array<AutomationTargetPair>
    >([{ organizationId: null, mode: 'live' }]);
    const [cloneSource, setCloneSource] =
        useState<AdminAgentAutomationGroupRead | null>(null);
    const [builderIntent, setBuilderIntent] = useState<BuilderIntent>('create');
    const [editGroup, setEditGroup] =
        useState<AdminAgentAutomationGroupRead | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const { isLoading: isLoadingOrganizations } = useAllAdminOrganizations({
        autoLoadPages: true,
        orgDataOnly: true,
    });

    const updateSearch = (patch: Partial<AdminAutomationsSearch>) => {
        void navigate({
            search: {
                ...search,
                ...patch,
                page: patch.page ?? 1,
            },
        });
    };

    const queryParams = useMemo(
        () => ({
            page: search.page,
            per_page: PER_PAGE,
            organization_id: search.organization_id,
            status: search.status as Array<AgentAutomationStatus> | undefined,
            mode: search.mode as Array<Mode> | undefined,
            trigger_type: search.trigger_type as
                | Array<AgentAutomationTriggerType>
                | undefined,
            search: search.search?.trim() || undefined,
        }),
        [
            search.mode,
            search.organization_id,
            search.page,
            search.search,
            search.status,
            search.trigger_type,
        ],
    );
    const { data, error, hasRequestFailed, isLoading, isValidating, mutate } =
        useAdminAgentAutomations(queryParams);
    const items = data?.items ?? [];
    const hasLoadError =
        error !== undefined || (hasRequestFailed && data === undefined);
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
    const handleAutomationChanged = useCallback(() => {
        void mutate();
    }, [mutate]);

    // Seed the create dialog's org dropdown: a clone copies its source
    // automation's target, while a fresh create picks up single-valued
    // filters when present.
    const openCreateDialog = (source: AdminAgentAutomationGroupRead | null) => {
        setCloneSource(source);
        setBuilderIntent(source === null ? 'create' : 'clone');
        setCreateTargetPairs([
            {
                organizationId:
                    source?.target_organizations[0]?.organization_id ??
                    getSingleValue(search.organization_id) ??
                    null,
                mode: source?.mode ?? 'live',
            },
        ]);
        setBuilderOpen(true);
    };

    const openAddTargetsDialog = (source: AdminAgentAutomationGroupRead) => {
        setCloneSource(source);
        setBuilderIntent('add_targets');
        setCreateTargetPairs([{ organizationId: null, mode: source.mode }]);
        setBuilderOpen(true);
    };

    const openEditDialog = (source: AdminAgentAutomationGroupRead) => {
        setEditGroup(source);
        setEditDialogOpen(true);
    };

    // Keep the edit dialog fed with the freshest row data after list
    // revalidations (e.g. following a membership change inside the dialog).
    const currentEditGroup =
        editGroup === null
            ? null
            : (items.find(
                  (item) =>
                      item.automation_group_id ===
                      editGroup.automation_group_id,
              ) ?? editGroup);

    const hasFilters =
        (search.organization_id?.length ?? 0) > 0 ||
        (search.status?.length ?? 0) > 0 ||
        (search.mode?.length ?? 0) > 0 ||
        (search.trigger_type?.length ?? 0) > 0 ||
        Boolean(search.search?.trim());

    return (
        <div className="flex h-full flex-col bg-[#F7FAFA] text-[#002429]">
            <div className="border-b border-[#DDE6E6] bg-white px-5 py-4">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-[17px] leading-6 font-semibold">
                            Agent Automations
                        </h2>
                        <p className="mt-0.5 text-sm text-[#5F7478]">
                            Admin Agent automation management across all
                            organizations.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="text-sm text-[#5F7478] tabular-nums">
                            {total} total
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#DDE6E6] bg-white shadow-none"
                            asChild={true}
                        >
                            <Link to="/admin/agent/automations/runs">Runs</Link>
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            className="h-8 bg-[#111111] px-3 text-white hover:bg-[#2B2B2B]"
                            onClick={() => openCreateDialog(null)}
                        >
                            <Plus className="h-4 w-4" />
                            Create
                        </Button>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative min-w-0 basis-full sm:min-w-64 sm:flex-[1_1_18rem] xl:max-w-md">
                        <Search className="absolute top-2.5 left-3 h-4 w-4 text-[#829494]" />
                        <Input
                            value={search.search ?? ''}
                            onChange={(event) =>
                                updateSearch({
                                    search: event.target.value || undefined,
                                })
                            }
                            placeholder="Search automations or orgs…"
                            className="h-8 border-[#DDE6E6] bg-[#FBFDFD] pl-9 text-sm shadow-none"
                        />
                    </div>
                    <div className="min-w-0 basis-full sm:min-w-64 sm:flex-[1_1_18rem] xl:max-w-md">
                        <OrganizationCombobox
                            value={search.organization_id ?? []}
                            onValueChange={(value) =>
                                updateSearch({ organization_id: value })
                            }
                            placeholder="Filter organizations…"
                            disabled={isLoadingOrganizations}
                        />
                    </div>
                    <Select
                        value={getSingleValue(search.status) ?? 'all'}
                        onValueChange={(value) =>
                            updateSearch({
                                status:
                                    value === 'all'
                                        ? undefined
                                        : [
                                              value as (typeof STATUS_OPTIONS)[number],
                                          ],
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-40 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={getSingleValue(search.mode) ?? 'all'}
                        onValueChange={(value) =>
                            updateSearch({
                                mode:
                                    value === 'all'
                                        ? undefined
                                        : [value as Mode],
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-36 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Mode" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All modes</SelectItem>
                            <SelectItem value="live">Live</SelectItem>
                            <SelectItem value="sandbox">Sandbox</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={getSingleValue(search.trigger_type) ?? 'all'}
                        onValueChange={(value) =>
                            updateSearch({
                                trigger_type:
                                    value === 'all'
                                        ? undefined
                                        : [value as AgentAutomationTriggerType],
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-44 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Trigger" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All triggers</SelectItem>
                            <SelectItem value="schedule">Schedule</SelectItem>
                            <SelectItem value="phoebe_event">Event</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex h-9 max-w-full shrink-0 items-center gap-2 whitespace-nowrap">
                        {hasFilters ? (
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                aria-label="Clear filters"
                                onClick={() =>
                                    void navigate({
                                        search: { page: 1 },
                                    })
                                }
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-auto p-5">
                <div className="overflow-hidden rounded-[8px] border border-[#DDE6E6] bg-white shadow-[0_1px_2px_rgba(0,36,41,0.04)]">
                    <Table
                        containerClassName="overflow-x-auto"
                        className="min-w-[1060px] table-fixed"
                    >
                        <colgroup>
                            <col className="w-[20%]" />
                            <col className="w-[15%]" />
                            <col className="w-[8%]" />
                            <col className="w-[7%]" />
                            <col className="w-[9%]" />
                            <col className="w-[18%]" />
                            <col className="w-[12%]" />
                            <col className="w-[7%]" />
                            <col className="w-[4%]" />
                        </colgroup>
                        <TableHeader className="bg-[#FBFDFD]">
                            <TableRow className="border-[#DDE6E6] hover:bg-transparent">
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Name
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Targets
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Status
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Mode
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Owner
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Triggers
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Next run
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Runs today
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Total runs
                                </TableHead>
                                <TableHead className="h-9 px-2 text-right text-[11px] font-semibold text-[#5F7478] uppercase">
                                    <span className="sr-only">Actions</span>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell
                                        colSpan={10}
                                        className="h-28 text-center"
                                    >
                                        <Spinner
                                            size="lg"
                                            className="mx-auto text-gray-500"
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {!isLoading && hasLoadError ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell
                                        colSpan={10}
                                        className="h-28 text-center text-sm text-red-600"
                                    >
                                        {formatErrorMessage(
                                            error,
                                            'Failed to load automations.',
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {!isLoading &&
                            !hasLoadError &&
                            items.length === 0 ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell
                                        colSpan={10}
                                        className="h-32 text-center"
                                    >
                                        <div className="space-y-2">
                                            <Workflow className="mx-auto h-8 w-8 text-gray-500" />
                                            <div className="font-medium">
                                                No automations found
                                            </div>
                                            <p className="text-muted-foreground text-sm">
                                                Adjust the filters or create an
                                                automation.
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {items.map((automation) => (
                                <AutomationRow
                                    key={automation.automation_group_id}
                                    automation={automation}
                                    onChanged={handleAutomationChanged}
                                    onAddTargets={() =>
                                        openAddTargetsDialog(automation)
                                    }
                                    onClone={() => openCreateDialog(automation)}
                                    onEdit={() => openEditDialog(automation)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm text-[#5F7478]">
                        Page {search.page} of {totalPages}
                        {isValidating && !isLoading ? ' · Refreshing' : ''}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#DDE6E6] bg-white shadow-none"
                            disabled={search.page <= 1}
                            onClick={() =>
                                updateSearch({ page: search.page - 1 })
                            }
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Previous
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#DDE6E6] bg-white shadow-none"
                            disabled={search.page >= totalPages}
                            onClick={() =>
                                updateSearch({ page: search.page + 1 })
                            }
                        >
                            Next
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
            <AutomationBuilderDialog
                automation={null}
                automationGroupId={
                    builderIntent === 'add_targets'
                        ? cloneSource?.automation_group_id
                        : null
                }
                cloneSource={cloneSource}
                fixedTargetMode={
                    builderIntent === 'add_targets'
                        ? cloneSource?.mode
                        : undefined
                }
                open={builderOpen}
                onOpenChange={setBuilderOpen}
                mode={createTargetPairs[0]?.mode ?? 'live'}
                organizationId={createTargetPairs[0]?.organizationId ?? null}
                onOrganizationIdChange={(organizationId) =>
                    setCreateTargetPairs((targets) => [
                        {
                            organizationId,
                            mode: targets[0]?.mode ?? 'live',
                        },
                        ...targets.slice(1),
                    ])
                }
                targetPairs={createTargetPairs}
                onTargetPairsChange={setCreateTargetPairs}
                onGroupSaved={(automation) => {
                    void mutate();
                    void navigate({
                        to: '/admin/agent/automations/groups/$automationGroupId',
                        params: {
                            automationGroupId: automation.automation_group_id,
                        },
                    });
                }}
            />
            {currentEditGroup ? (
                <AutomationBuilderDialog
                    automation={null}
                    adminGroup={currentEditGroup}
                    open={editDialogOpen}
                    onOpenChange={setEditDialogOpen}
                    mode={currentEditGroup.mode}
                    organizationId={null}
                    onGroupSaved={() => void mutate()}
                />
            ) : null}
        </div>
    );
}

function AutomationRow({
    automation,
    onAddTargets,
    onChanged,
    onClone,
    onEdit,
}: {
    readonly automation: AdminAgentAutomationGroupRead;
    readonly onAddTargets: () => void;
    readonly onChanged: () => void;
    readonly onClone: () => void;
    readonly onEdit: () => void;
}) {
    const targetOrganizations = automation.target_organizations;
    const timezone =
        automation.target_organizations[0]?.organization_timezone ?? 'UTC';
    const targetOrganizationNames = targetOrganizations.map(
        (target) =>
            target.organization_internal_name?.trim() ||
            target.organization_name,
    );
    const visibleTargetOrganizationNames = targetOrganizationNames.slice(0, 3);
    const hiddenTargetOrganizationCount =
        targetOrganizationNames.length - visibleTargetOrganizationNames.length;
    const triggerLabel = triggerSummary(automation, timezone);
    const nextFireAt = targetOrganizations
        .map((target) => target.schedule?.next_fire_at ?? null)
        .filter((value): value is string => value !== null)
        .sort()[0];

    return (
        <TableRow className="h-14 border-[#E6EEEE] hover:bg-[#F7FAFA]">
            <TableCell className="px-3 py-2">
                <Link
                    to="/admin/agent/automations/groups/$automationGroupId"
                    params={{
                        automationGroupId: automation.automation_group_id,
                    }}
                    className="block truncate text-[13px] font-semibold text-[#002429] hover:underline"
                >
                    {automation.name}
                </Link>
            </TableCell>
            <TableCell className="px-3 py-2">
                <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <Badge
                            variant="outline"
                            className="border-[#DDE6E6] text-[#253F44]"
                        >
                            {targetOrganizations.length}{' '}
                            {targetOrganizations.length === 1 ? 'org' : 'orgs'}
                        </Badge>
                        <span className="truncate text-[13px] font-medium text-[#253F44]">
                            {visibleTargetOrganizationNames.join(', ')}
                            {hiddenTargetOrganizationCount > 0
                                ? ` +${hiddenTargetOrganizationCount}`
                                : ''}
                        </span>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-6 px-2 text-xs"
                        onClick={onAddTargets}
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Add orgs
                    </Button>
                </div>
            </TableCell>
            <TableCell className="px-3 py-2">
                {/* Rows represent automation groups, so show the group's
                    master switch rather than one member's own status. */}
                <StatusBadge status={automation.group_status} />
            </TableCell>
            <TableCell className="px-3 py-2">
                <Badge
                    variant="outline"
                    className="border-[#DDE6E6] text-[#253F44]"
                >
                    {formatAutomationModeLabel(automation.mode)}
                </Badge>
            </TableCell>
            <TableCell className="truncate px-3 py-2 text-[13px] text-[#253F44]">
                {automation.owner_name ?? 'Owner'}
            </TableCell>
            <TableCell className="px-3 py-2">
                <div
                    className="truncate text-[13px] text-[#253F44]"
                    title={triggerLabel}
                >
                    {triggerLabel}
                </div>
            </TableCell>
            <TableCell className="truncate px-3 py-2 text-[13px] text-[#253F44]">
                {formatOptionalDate(nextFireAt ?? null, timezone)}
            </TableCell>
            <TableCell className="px-3 py-2 text-[13px] text-[#253F44]">
                {automation.runs_today}
            </TableCell>
            <TableCell className="px-3 py-2 text-[13px] text-[#253F44]">
                {automation.total_runs}
            </TableCell>
            <TableCell className="px-2 py-2 text-right">
                <AutomationGroupActions
                    group={automation}
                    layout="menu"
                    onChanged={onChanged}
                    onDeleted={onChanged}
                    onClone={onClone}
                    onEdit={onEdit}
                />
            </TableCell>
        </TableRow>
    );
}
